export interface NormalizedPhoneIdentity {
  e164: string;
  nationalDigits: string;
  countryCallingCode: string;
}

export type PhoneNormalizationResult =
  | {
      valid: true;
      identity: NormalizedPhoneIdentity;
    }
  | {
      valid: false;
      reason:
        | 'EMPTY'
        | 'INVALID_CHARACTERS'
        | 'INVALID_LENGTH'
        | 'COUNTRY_CODE_REQUIRED';
    };

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Normalizes North American numbers by default while accepting an
 * explicitly supplied international E.164 number.
 *
 * A contact phone number is a verified contact channel. It is not a
 * KinsCircle account, profile, membership, or entitlement authority.
 */
export function normalizePhoneToE164(
  rawPhone: string,
  defaultCountryCallingCode = '1',
): PhoneNormalizationResult {
  const trimmed = rawPhone.trim();

  if (!trimmed) {
    return {
      valid: false,
      reason: 'EMPTY',
    };
  }

  if (!/^[+\d\s().-]+$/.test(trimmed)) {
    return {
      valid: false,
      reason: 'INVALID_CHARACTERS',
    };
  }

  const rawDigits = digitsOnly(trimmed);

  if (trimmed.startsWith('+')) {
    if (rawDigits.length < 8 || rawDigits.length > 15) {
      return {
        valid: false,
        reason: 'INVALID_LENGTH',
      };
    }

    return {
      valid: true,
      identity: Object.freeze({
        e164: `+${rawDigits}`,
        nationalDigits: rawDigits,
        countryCallingCode: rawDigits.slice(
          0,
          Math.max(1, rawDigits.length - 10),
        ),
      }),
    };
  }

  const countryCode = digitsOnly(defaultCountryCallingCode);

  if (!countryCode) {
    return {
      valid: false,
      reason: 'COUNTRY_CODE_REQUIRED',
    };
  }

  let nationalDigits = rawDigits;

  if (
    countryCode === '1' &&
    nationalDigits.length === 11 &&
    nationalDigits.startsWith('1')
  ) {
    nationalDigits = nationalDigits.slice(1);
  }

  if (
    nationalDigits.length < 7 ||
    `${countryCode}${nationalDigits}`.length > 15
  ) {
    return {
      valid: false,
      reason: 'INVALID_LENGTH',
    };
  }

  return {
    valid: true,
    identity: Object.freeze({
      e164: `+${countryCode}${nationalDigits}`,
      nationalDigits,
      countryCallingCode: countryCode,
    }),
  };
}

export async function hashContactIdentity(
  normalizedValue: string,
): Promise<string> {
  const canonicalValue = normalizedValue.trim().toLowerCase();

  if (!canonicalValue) {
    throw new Error('Contact identity cannot be empty.');
  }

  const bytes = new TextEncoder().encode(canonicalValue);
  const digest = await crypto.subtle.digest('SHA-256', bytes);

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export const CONTACT_IDENTITY_RUNTIME_READY = true;
